/**
 * The worklist↔workbench round trip's provenance (GDN-3197), riding
 * react-router `location.state`.
 *
 * Outbound: a worklist row-click records WHERE the biller came from — the
 * full list URL (which, post GDN-3196, IS the saved list state) and a label
 * the workbench's back button can say truthfully. Inbound (back): the
 * workbench records WHICH claim was worked and the state it was last known
 * to be in, so the list can anchor the eye — flash the row when it's still
 * there, explain honestly when it left the view.
 *
 * Both readers are TOLERANT: history state is unowned data (it survives
 * refreshes, other code can write it), so anything malformed reads as
 * absent — and absence routes to the fallback, never a crash. Lost state
 * (true direct links, new tabs) is exactly when the fallback SHOULD apply.
 */

/**
 * One claim's place in a queue: enough to build its workbench URL and no more.
 *
 * `encounterId` is REQUIRED, and that is a rule about what a run can contain
 * rather than a convenience. A claim with no encounter cannot be opened in the
 * workbench at all — the `/jotbill/claim/:id` gate resolves no encounter to
 * forward to and renders the legacy claim view, which has no queue rail and no
 * Next. Putting such a claim in the sequence would promise a step that lands
 * the biller somewhere they cannot continue from. They are excluded at capture
 * instead; the "loaded" qualifier then admits the run is smaller than the view.
 */
export interface WorkbenchQueueEntry {
  claimId: string;
  encounterId: string;
}

/**
 * THE QUEUE (GDN-3322) — a run of claim work, frozen at the row click.
 *
 * `entries` is the sequence the biller SAW: the rows loaded in the list, in
 * table order, captured once. Deliberately not a live re-query — submitting a
 * claim removes it from "Ready to submit", so a queue that re-read itself
 * would renumber under the biller mid-run and change what "next" means
 * between the decision and the click.
 *
 * There is no `index` here on purpose. Position is DERIVED from the claim the
 * URL names against this sequence (`deriveQueueContext`), so the address bar
 * and the header cannot disagree — a carried index is a second copy of a fact
 * the URL already states, and back/forward navigation is exactly where the
 * two would drift apart.
 */
export interface WorkbenchQueue {
  /** The product's own words for this queue — a `WORK_VIEWS` label or the
   *  saved view's name. Never invented here. */
  label: string;
  /** The work view's key, for telemetry. Null when a saved view is active. */
  viewKey: string | null;
  /** The saved view's id, for telemetry. Null when a work view is active. */
  savedViewId: string | null;
  /**
   * The live run behind a Home queue (GDN-3379): the cache entry the
   * workbench reads the rest of the queue from as its pages land. Null for a
   * run captured whole, which is every other origin's. With a run named,
   * `entries` is its first page — the fallback for a reload that finds the
   * cache gone, not the whole run.
   */
  runId: string | null;
  entries: WorkbenchQueueEntry[];
  /** The server's count for the list this was captured from — the honest
   *  denominator when fewer rows were loaded than the view holds. Null when
   *  the count was not available. */
  viewCount: number | null;
  /** Advancing actions taken in this run. Carried so it survives Prev/Next. */
  handled: number;
  /** The last claim's advancing action has been taken — the run is over. */
  done: boolean;
}

export interface WorkbenchFrom {
  /** Full URL to return to — a list's GDN-3196 params, or an encounter view. */
  url: string;
  /**
   * Where the biller came from, in the product's own words.
   *
   * 'Home', 'Claims' and 'Encounters' are the three WORK SURFACES.
   * 'Encounter' (singular) is a single encounter's own page — the claim tab
   * there is a same-route switch, so before GDN-3322 it carried no state at
   * all and the workbench sent the biller to the Claims worklist, somewhere
   * they had never been. The words are the page's own: it calls itself
   * "Encounter" on its card and offers "Back to encounter" from the CMS-1500
   * route, and JotBill's first tab calls itself "Home".
   */
  label: 'Home' | 'Claims' | 'Encounters' | 'Encounter';
  /**
   * The queue this claim was opened from, or null.
   *
   * Null is the ordinary case, not a degraded one: direct links, new tabs,
   * the Encounters worklist and every other entry point carry no queue, and
   * the page renders exactly as it did before this existed.
   */
  queue: WorkbenchQueue | null;
}

export interface WorklistReturn {
  returnedClaimId: string;
  /** The claim's work-state label as the workbench last knew it (its
   *  `work_summary`) — null when no canonical row was available. */
  returnedStateLabel: string | null;
}

/**
 * The no-provenance default: JotBill Home (ADR-010).
 *
 * This used to be the Claims worklist, chosen because landing billers on
 * Encounters was an accident of /jotbill's old default tab rather than a
 * decision. Home replaces that reasoning rather than contradicting it: bare
 * /jotbill is now a deliberate work surface that answers "what needs me
 * right now", so a biller who arrived by direct link or a new tab and pressed
 * Back lands where the day starts, not in a list they never opened.
 */
export const WORKBENCH_BACK_FALLBACK = {
  url: '/jotbill',
  label: 'Home',
} as const;

export function workbenchProvenanceState(
  from: Omit<WorkbenchFrom, 'queue'> & { queue?: WorkbenchQueue | null },
): { workbenchFrom: WorkbenchFrom } {
  return { workbenchFrom: { ...from, queue: from.queue ?? null } };
}

/**
 * The encounter page as an origin (GDN-3322).
 *
 * Built from the location the biller is on AT THE MOMENT OF THE SWITCH, so it
 * keeps whichever encounter view they were in — overview, payments, whatever
 * tab and query it carried. The claim workspace itself is stripped: a return
 * URL that re-entered the workspace they just left would be a back control
 * that looks broken.
 *
 * Never carries a queue. A queue is a run of claim work captured in the Claims
 * worklist or on a Home queue card; an encounter is one encounter, and
 * `readQueue` refuses a queue on either encounter origin anyway.
 */
export function encounterOriginState(location: { pathname: string; search: string }): {
  workbenchFrom: WorkbenchFrom;
} {
  const params = new URLSearchParams(location.search);
  if (params.get('workspace') === 'claim') {
    params.delete('workspace');
    /* The claim it pinned goes with it — it names a claim, not a view. */
    params.delete('claim_id');
  }
  const query = params.toString();
  return workbenchProvenanceState({
    label: 'Encounter',
    url: query ? `${location.pathname}?${query}` : location.pathname,
  });
}

/**
 * The state a same-route switch INTO the claim workspace should carry.
 *
 * One rule, in one place, because both switches on the encounter page and the
 * workbench's own family thread need it and must not disagree:
 *
 * - an origin already on this history entry WINS. Arriving from a worklist and
 *   then moving between panels, or walking the claim family, must never
 *   rewrite where the biller came from — the claim changed, their route here
 *   did not.
 * - otherwise this page IS the origin, because they were on it.
 *
 * `readWorkbenchFrom` is what decides "already has one", so a malformed origin
 * is replaced rather than carried — the same tolerance every other reader here
 * applies.
 */
export function claimWorkspaceOriginState(location: {
  pathname: string;
  search: string;
  state?: unknown;
}): unknown {
  return readWorkbenchFrom(location.state)
    ? location.state
    : encounterOriginState({ pathname: location.pathname, search: location.search });
}

/** A finite number, or null. History state can hold anything. */
function readCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readEntries(value: unknown): WorkbenchQueueEntry[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const entries: WorkbenchQueueEntry[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') return null;
    const { claimId, encounterId } = raw as Record<string, unknown>;
    if (typeof claimId !== 'string' || !claimId) return null;
    /* Both ids or no queue. Dropping the offending entry instead would shift
       every position after it, which is the one thing the rail must never do
       quietly — and nothing writes such an entry, so this is corrupt state,
       not a shape to degrade gracefully into. */
    if (typeof encounterId !== 'string' || !encounterId) return null;
    entries.push({ claimId, encounterId });
  }
  return entries;
}

/**
 * The queue, read the way the rest of this file reads history state: a
 * malformed queue is ABSENT, and absence is the page as it shipped before
 * queues existed. It never takes the provenance around it down with it — the
 * back button must keep working for every tab open across a deploy.
 */
function readQueue(value: unknown, label: WorkbenchFrom['label']): WorkbenchQueue | null {
  /* The two surfaces that CAPTURE a run: the Claims worklist and Home's
     queue cards. A queue arriving on an Encounters or Encounter return is
     not a queue we wrote, so it is not one we honour. */
  if (label !== 'Claims' && label !== 'Home') return null;
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const entries = readEntries(raw.entries);
  if (!entries) return null;
  if (typeof raw.label !== 'string' || !raw.label) return null;
  return {
    entries,
    /* Bookkeeping is COERCED, not trusted: a nonsense `handled` must not be
       able to put a number in front of a biller. Its floor is what a fresh
       run would say. */
    done: raw.done === true,
    handled: Math.max(0, Math.trunc(readCount(raw.handled) ?? 0)),
    label: raw.label,
    /* A run id that is not a non-empty string names no run, and the
       sequence carried beside it is the whole run — the page before GDN-3379. */
    runId: typeof raw.runId === 'string' && raw.runId ? raw.runId : null,
    savedViewId: typeof raw.savedViewId === 'string' ? raw.savedViewId : null,
    viewCount: readCount(raw.viewCount),
    viewKey: typeof raw.viewKey === 'string' ? raw.viewKey : null,
  };
}

export function readWorkbenchFrom(state: unknown): WorkbenchFrom | null {
  if (!state || typeof state !== 'object') return null;
  const from = (state as Record<string, unknown>).workbenchFrom;
  if (!from || typeof from !== 'object') return null;
  const { label, url } = from as Record<string, unknown>;
  /* Same-origin paths only — decided by the SAME algorithm the browser
     uses, not by string patterns. Three review rounds each found one more
     WHATWG quirk that makes a '/'-prefixed string resolve cross-origin
     (`//host`, `\` treated as `/`, tab/newline stripped before parsing),
     where pushState throws a SecurityError on back-click. Pattern checks
     can't close that class; parsing with `new URL` against the current
     origin does, because whatever quirk exists, the verdict here and the
     resolution at navigate-time agree by construction. The explicit
     backslash/control-character reject stays as belt and braces — no
     legitimate list URL contains them. */
  if (typeof url !== 'string' || !url.startsWith('/') || /[\\\t\n\r]/.test(url)) return null;
  try {
    if (new URL(url, window.location.origin).origin !== window.location.origin) return null;
  } catch {
    return null;
  }
  if (label !== 'Home' && label !== 'Claims' && label !== 'Encounters' && label !== 'Encounter') {
    return null;
  }
  return { url, label, queue: readQueue((from as Record<string, unknown>).queue, label) };
}

export function worklistReturnState(returned: WorklistReturn): { worklistReturn: WorklistReturn } {
  return { worklistReturn: returned };
}

export function readWorklistReturn(state: unknown): WorklistReturn | null {
  if (!state || typeof state !== 'object') return null;
  const returned = (state as Record<string, unknown>).worklistReturn;
  if (!returned || typeof returned !== 'object') return null;
  const { returnedClaimId, returnedStateLabel } = returned as Record<string, unknown>;
  if (typeof returnedClaimId !== 'string' || !returnedClaimId) return null;
  return {
    returnedClaimId,
    returnedStateLabel: typeof returnedStateLabel === 'string' ? returnedStateLabel : null,
  };
}
